import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchProviders, createProvider, updateProvider, deleteProvider } from '../services/api';
import EnrollModal from '../components/EnrollModal';
import { AdminTableSkeleton } from '../components/Skeleton';
import './Admin.css';
import './Providers.css';

const EMPTY_FORM = { name: '', npi: '' };

function Providers() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [modalMode, setModalMode]   = useState(null); // 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [modalError, setModalError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const [enrollTarget, setEnrollTarget] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      const data = await fetchProviders();
      setProviders(data.data || []);
    } catch (err) {
      console.error('Failed to load providers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.npi || '').includes(q)
    );
  }, [providers, searchQuery]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModalError(null);
    setModalMode('create');
  };

  const openEdit = (provider) => {
    setEditTarget(provider);
    setForm({ name: provider.name || '', npi: provider.npi || '' });
    setModalError(null);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setModalError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setModalError('Provider name is required.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try {
      if (modalMode === 'create') {
        const data = await createProvider(form);
        setProviders((prev) => [...prev, data.data].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const data = await updateProvider(editTarget.id, form);
        setProviders((prev) =>
          prev.map((p) => (p.id === editTarget.id ? data.data : p))
        );
      }
      closeModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProvider(deleteTarget.id);
      setProviders((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  if (loading) {
    return <AdminTableSkeleton columns={3} rows={6} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Provider Directory</h1>
          <p>Save rendering and referring providers for quick selection when creating claims</p>
        </div>
        <button className="admin-create-btn" onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Provider
        </button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by name or NPI..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">
          {filtered.length} {filtered.length === 1 ? 'provider' : 'providers'}
        </span>
      </div>

      <div className="admin-table-card">
        {filtered.length === 0 ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <polyline points="17 11 19 13 23 9"/>
            </svg>
            <h3>{searchQuery ? 'No providers match your search' : 'No providers yet'}</h3>
            <p>{searchQuery ? 'Try a different name or NPI' : 'Add providers to quickly fill in rendering and referring provider fields when creating claims'}</p>
          </div>
        ) : (
          <table className="admin-table provider-table">
            <thead>
              <tr>
                <th>Provider Name</th>
                <th>NPI</th>
                <th className="provider-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((provider) => (
                <tr key={provider.id}>
                  <td>
                    <div className="admin-name-cell">
                      <div className="admin-avatar provider-avatar">
                        {provider.name.split(/[\s,]+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <span>{provider.name}</span>
                    </div>
                  </td>
                  <td>
                    {provider.npi
                      ? <span className="provider-npi-badge">{provider.npi}</span>
                      : <span className="provider-no-value">—</span>}
                  </td>
                  <td>
                    <div className="provider-actions">
                      <button className="admin-action-btn provider-enroll-btn" onClick={() => setEnrollTarget(provider)} title="Enroll this provider with a payer through Claim.MD">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
                        </svg>
                        Enroll
                      </button>
                      <button className="admin-action-btn provider-edit-btn" onClick={() => openEdit(provider)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                      <button className="admin-action-btn deactivate" onClick={() => setDeleteTarget(provider)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalMode && (
        <div className="admin-modal-overlay">
          <div className="admin-modal provider-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <polyline points="17 11 19 13 23 9"/>
                </svg>
              </div>
              <div className="admin-modal-header-text">
                <h2>{modalMode === 'create' ? 'Add Provider' : 'Edit Provider'}</h2>
                <p>
                  {modalMode === 'create'
                    ? 'Add a provider to the directory for quick claim entry'
                    : `Editing ${editTarget?.name}`}
                </p>
              </div>
            </div>

            {modalError && (
              <div className="admin-modal-alert error" style={{ marginBottom: 18 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSave} className="admin-modal-form">
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="provider-name">Provider Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="provider-name"
                  type="text"
                  className="admin-modal-input"
                  placeholder="e.g. Smith, John or John Smith"
                  autoFocus
                  {...field('name')}
                />
              </div>

              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="provider-npi">NPI</label>
                <input
                  id="provider-npi"
                  type="text"
                  className="admin-modal-input"
                  placeholder="10-digit NPI"
                  maxLength={10}
                  {...field('npi')}
                  onChange={(e) => setForm((f) => ({ ...f, npi: e.target.value.replace(/\D/g, '') }))}
                />
                <span className="provider-field-hint">National Provider Identifier — numbers only</span>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-modal-btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-modal-btn-submit" disabled={saving}>
                  {saving && <span className="admin-modal-spinner" />}
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Add Provider' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Delete Provider?</h3>
            <p>
              Are you sure you want to remove <strong>{deleteTarget.name}</strong> from the directory? This won&apos;t affect any existing claims.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="admin-confirm-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Provider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payer Enrollment Modal */}
      {enrollTarget && (
        <EnrollModal provider={enrollTarget} onClose={() => setEnrollTarget(null)} />
      )}
    </div>
  );
}

export default Providers;
